import React, { useState } from 'react';

import { Flex, Link } from '@circleci/compass';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';

import CountrySelect from '~/components/NewsletterSignup/CountrySelect';
import SubscribeButton from '~/components/NewsletterSignup/SubscribeButton';
import Container from '~/components/shared/Container';
import * as screenSizes from '~/components/shared/viewPorts';
import { useAnalytics, Action } from '~/hooks/useAnalytics';
import { styled } from '~/styled';

const NewsletterSignupBackground = styled.section(({ theme }) => {
  return `
  padding-bottom: ${theme.space.space64};
  padding-top: ${theme.space.space64};
  background-color: ${theme.colors.white}};
`;
});
const OuterRow = styled(Flex)`
  flex-wrap: wrap;
  ${screenSizes.mediaHugeAndLarger} {
    flex-wrap: nowrap;
  }
`;

const Form = styled.form`
  width: 100%;
  ${screenSizes.mediaHugeAndLarger} {
    width: 55%;
  }
`;

const Email = styled.input(({ theme }) => {
  return `
  color: ${theme.colors.n500};
  font-size: ${theme.space.space16};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.n90};
  font-weight: ${theme.fontWeights.regular};
  height: ${theme.space.space40};
  flex-grow: 2;
  padding: ${theme.space.space16} ${theme.space.space12};
  margin: ${theme.space.space8} ${theme.space.space16}
    ${theme.space.space8} 0;
  box-shadow: none;
  border-radius: ${theme.radii.rounded};
  line-height: ${theme.space.space24};
  ${screenSizes.mediaMaxWidthSmall} {
    margin-right: 0;
  }
  &::placeholder {
    color: ${theme.colors.n100};
  }
  .hasError &:invalid {
    border-color: #f24646; // not in theme
    box-shadow: 0 0 ${theme.space.space4} #f24646; // not in theme
  }
  &:valid {
    border-color: #5fc890; // not in theme
    box-shadow: 0 0 ${theme.space.space4} #5fc890; // not in theme
  }
`;
});

const SubscribeButtonWrapper = styled.div(({ theme }) => {
  return `
  ${screenSizes.mediaTabletAndLarger} {
    margin-left: ${theme.space.space16}
  }
  `;
});

const JoinPanel = styled(Flex)(({ theme }) => {
  return `
  margin: 0;
  width: 100%;
  margin-bottom: 1em;
  flex-shrink: 0;
  color: ${theme.colors.n70};
  font-size: ${theme.fontSizes.fontsize20};
  font-weight: ${theme.fontWeights.medium};
  line-height: 1.4;
  ${screenSizes.mediaHugeAndLarger} {
    width: 50%;
    max-width: 410px;
    margin-bottom: 0;
  }
`;
});

const ResearchPanelExternalLink = styled(Link)(({ theme }) => {
  return `
  color: ${theme.colors.blue500};
  font-size: ${theme.fontSizes.fontsize16};
  font-weight: ${theme.fontWeights.regular};
  line-height: ${theme.lineHeights.lineheight24};
  padding-left: ${theme.space.space4};
  padding-right: ${theme.space.space4};
`;
});

const TermsAndPolicyExternalLink = styled(Link)(({ theme }) => {
  return `
  color: ${theme.colors.blue500};
  font-size: ${theme.fontSizes.fontsize14};
  font-weight: ${theme.fontWeights.regular};
  line-height: ${theme.lineHeights.lineheight24};
  padding-left: ${theme.space.space4};
  padding-right: ${theme.space.space4};
`;
});

const SubHeading = styled.div(({ theme }) => {
  return `
  margin: 0;
  width: 100%;
  flex-shrink: 0;
  color: ${theme.colors.n900};
  font-size: ${theme.fontSizes.fontsize20};
  font-weight: ${theme.fontWeights.medium};
  line-height: ${theme.lineHeights.lineheight28};
`;
});

const ResearchPanel = styled.div(({ theme }) => {
  return `
  margin: 0;
  width: 100%;
  flex-shrink: 0;
  margin-top: ${theme.space.space4};
  color: ${theme.colors.n600};
  font-size: ${theme.fontSizes.fontsize16};
  font-weight: ${theme.fontWeights.regular};
  line-height: ${theme.lineHeights.lineheight24};
`;
});

const HeadingFormattedMessage = styled.div(({ theme }) => {
  return `
  border-bottom: 1px ${theme.colors.n80} solid;
  margin-bottom: ${theme.space.space24};

  color: ${theme.colors.n900};
  font-size: ${theme.fontSizes.fontsize24};
  font-weight: ${theme.fontWeights.medium};
  line-height: ${theme.lineHeights.lineheight32};
`;
});

const TermsAndCondition = styled.div(({ theme }) => {
  return `
  color: ${theme.colors.n400};
  font-size: ${theme.fontSizes.fontsize14};
  font-weight: ${theme.fontWeights.regular};
  line-height: ${theme.lineHeights.lineheight24};
`;
});

export const NewsletterSignup = () => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [emailValid, setEmailValidity] = useState(false);
  const [countryValid, setCountryValidity] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const { trackStandardizedAction } = useAnalytics();
  const handleClick = (event: string, text: string) => {
    trackStandardizedAction(event, Action.clicked, {
      text,
    });
  };

  const handleSuccess = () => {
    trackStandardizedAction('newsletter-subscribe-button', Action.submitted);
    setEmail('');
    setCountry('');
    setEmailValidity(false);
    setCountryValidity(false);
    setError(false);
    setSuccess(true);
  };

  const submitValue = () => {
    const bodyFormData = new FormData();
    bodyFormData.append('email', email);
    bodyFormData.append('country', country);
    bodyFormData.append('newsletter', 'devhub');
    axios
      .post('https://formtomarketo.circleci.com/newsletter', bodyFormData)
      .then(function (response) {
        if (response.status == 200) {
          handleSuccess();
        }
      })
      .catch(function () {
        setError(true);
      });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    const valid = e.target.checkValidity();
    const value = e.target.value;
    setSuccess(false);
    setError(false);

    if (type == 'email') {
      setEmail(value);
      setEmailValidity(valid);
    }
    if (type == 'country') {
      setCountry(value);
      setCountryValidity(valid);
      trackStandardizedAction('newsletter-country-selected', Action.changed);
    }
  };

  const subscribeBtnHandleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSuccess(false);
    if (emailValid && countryValid) {
      submitValue();
      trackStandardizedAction(
        'newsletter-succesfully-subscribed',
        Action.submitted,
      );
    } else {
      setError(true);
    }
  };

  return (
    <NewsletterSignupBackground>
      <Container>
        <HeadingFormattedMessage>
          <FormattedMessage id="newsletterSignup.heading" />
        </HeadingFormattedMessage>
        <OuterRow justify="space-between" direction={['row', 'row', 'row']}>
          <JoinPanel direction={['column', 'column', 'column']}>
            <SubHeading>
              <FormattedMessage id="newsletterSignup.subheading" />
            </SubHeading>
            <ResearchPanel className="ResearchPanel">
              <FormattedMessage
                id="newsletterSignup.joinPanel"
                defaultMessage="Or <join>join our research panel</join> and give feedback"
                values={{
                  join: (chunks) => {
                    return (
                      <ResearchPanelExternalLink
                        onClick={() => {
                          return handleClick(
                            'dd_devhub_newsletter-terms-link',
                            'join-our-research-panel',
                          );
                        }}
                        href={intl.formatMessage({
                          id: 'newsletterSignup.joinLink',
                        })}
                      >
                        {chunks}
                      </ResearchPanelExternalLink>
                    );
                  },
                }}
              />
            </ResearchPanel>
          </JoinPanel>
          <Form className={error ? 'hasError' : null} role="form">
            <Flex direction={['column', 'column', 'column']}>
              <Flex direction={['column', 'column', 'row']}>
                <Email
                  placeholder={intl.formatMessage({
                    id: 'newsletterSignup.email',
                  })}
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => {
                    return handleChange(e, 'email');
                  }}
                  onClick={() => {
                    return trackStandardizedAction(
                      'newsletter-email-input',
                      Action.clicked,
                    );
                  }}
                  data-testid={'email-input'}
                />
                <CountrySelect
                  onChange={(e) => {
                    return handleChange(e, 'country');
                  }}
                  value={country}
                />
                <SubscribeButtonWrapper>
                  <SubscribeButton onClick={subscribeBtnHandleClick} />
                </SubscribeButtonWrapper>
              </Flex>
              <TermsAndCondition>
                <FormattedMessage
                  id="newsletterSignup.terms"
                  defaultMessage="By submitting this form, you are agreeing to our <terms>Terms of Use</terms> and <privacy>Privacy Policy</privacy>."
                  values={{
                    terms: (chunks) => {
                      return (
                        <TermsAndPolicyExternalLink
                          onClick={() => {
                            return handleClick(
                              'dd_devhub_newsletter-terms-link',
                              'terms-of-Use',
                            );
                          }}
                          href={intl.formatMessage({
                            id: 'newsletterSignup.termsLink',
                          })}
                        >
                          {chunks}
                        </TermsAndPolicyExternalLink>
                      );
                    },
                    privacy: (chunks) => {
                      return (
                        <TermsAndPolicyExternalLink
                          onClick={() => {
                            return handleClick(
                              'dd_devhub_newsletter-terms-link',
                              'privacy-policy',
                            );
                          }}
                          href={intl.formatMessage({
                            id: 'newsletterSignup.privacyLink',
                          })}
                        >
                          {chunks}
                        </TermsAndPolicyExternalLink>
                      );
                    },
                  }}
                />
                {success && (
                  <span data-testid="newsletter-success">
                    <br />
                    ✓ <FormattedMessage id="newsletterSignup.success" />
                  </span>
                )}
                {error && (
                  <span data-testid="newsletter-error">
                    <br />
                    <FormattedMessage id="newsletterSignup.error" />
                  </span>
                )}
              </TermsAndCondition>
            </Flex>
          </Form>
        </OuterRow>
      </Container>
    </NewsletterSignupBackground>
  );
};
